import React, { useEffect, useMemo, useState } from "react";
import {
  getReglementsArbitrage,
  deleteReglementArbitrage,
  reorderReglementsArbitrage,
} from "../../../services/reglementarbitrageServices";
import Button from "../../../components/Button/Button";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { useNotification } from "../../../components/Notification/NotificationManager";
import ReglementArbitrageModal from "./ReglementArbitrageModal";

import {
  FaPlus,
  FaSearch,
  FaBookOpen,
  FaLink,
  FaImage,
  FaPen,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaSyncAlt,
  FaGavel,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { TbTriangleFilled, TbTriangleInvertedFilled } from "react-icons/tb";

/* ================= Helpers ================= */
const PAGE_SIZE = 20;

const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const stripHtml = (html = "") =>
  String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const short = (s = "", n = 140) => {
  const t = String(s || "").trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n)}…`;
};

const normalizeImageUrl = (url) => {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("//")) {
    return trimmed;
  }

  if (trimmed.startsWith("/")) {
    return `${window.location.origin}${trimmed}`;
  }

  return `${window.location.origin}/${trimmed}`;
};

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return v;
};

const applyFiltersAndSort = (reglements, term) => {
  let list = Array.isArray(reglements) ? [...reglements] : [];
  const q = normalize(term);

  if (q) {
    list = list.filter((item) =>
      normalize(
        [
          item.title,
          stripHtml(item.content),
          item.lien_url,
          item.lien_url_image,
        ]
          .filter(Boolean)
          .join(" ")
      ).includes(q)
    );
  }

  list.sort((a, b) => {
    const orderA = Number(a.display_order ?? 0);
    const orderB = Number(b.display_order ?? 0);
    if (orderA !== orderB) return orderA - orderB;
    return Number(a.id ?? 0) - Number(b.id ?? 0);
  });

  return list;
};

/* ================= UI atoms ================= */
function CompactStat({ icon, label, value, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    rose:
      "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.16em] opacity-75">
            {label}
          </div>
          <div className="mt-0.5 text-xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminPageHeader({
  totalReglements,
  withLinksCount,
  withImagesCount,
  textOnlyCount,
}) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Administration
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
              Règlements d’arbitrage
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              Gérez les règlements d’arbitrage disponibles pour les arbitres et
              commissaires sportifs, leur contenu, leurs liens utiles et leur
              ordre d’affichage.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:min-w-[820px]">
            <CompactStat
              icon={<FaGavel />}
              label="Règlements"
              value={totalReglements}
              accent="indigo"
            />
            <CompactStat
              icon={<FaLink />}
              label="Avec lien"
              value={withLinksCount}
              accent="emerald"
            />
            <CompactStat
              icon={<FaImage />}
              label="Avec image"
              value={withImagesCount}
              accent="amber"
            />
            <CompactStat
              icon={<FaBookOpen />}
              label="Texte seul"
              value={textOnlyCount}
              accent="rose"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FiltersBar({
  searchTerm,
  setSearchTerm,
  onReset,
  onRefresh,
  onCreate,
  loading,
}) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr),auto]">
        <div className="relative">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un règlement par titre, contenu ou lien..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Réinitialiser
          </button>

          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaSyncAlt />
            Rafraîchir
          </button>

          <Button
            text="Ajouter un règlement"
            onClick={onCreate}
            icon={FaPlus}
            className="!h-11 !rounded-2xl !px-4"
          />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
        <FaGavel className="text-2xl" />
      </div>

      <h3 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
        Aucun règlement trouvé
      </h3>

      <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600 dark:text-slate-300">
        Aucun règlement ne correspond à votre recherche actuelle. Vous pouvez
        modifier votre recherche ou créer un nouveau règlement d’arbitrage.
      </p>

      <div className="mt-5">
        <Button
          text="Créer un règlement"
          onClick={onCreate}
          icon={FaPlus}
          className="!rounded-2xl !px-5 !py-2.5"
        />
      </div>
    </div>
  );
}

function PreviewImage({ src, alt }) {
  const normalized = normalizeImageUrl(src);
  const [imgSrc, setImgSrc] = useState(normalized);

  useEffect(() => {
    setImgSrc(normalized);
  }, [normalized]);

  if (!normalized || !imgSrc) {
    return (
      <div className="flex h-20 w-32 items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500">
        <FaImage className="text-xl" />
      </div>
    );
  }

  return (
    <img
      src={imgSrc}
      alt={alt}
      onError={() => setImgSrc(null)}
      className="h-20 w-32 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
    />
  );
}

function OrderControl({
  reglement,
  index,
  total,
  isOrdering,
  onMoveUp,
  onMoveDown,
}) {
  return (
    <div className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 dark:border-slate-700 dark:bg-slate-800">
      <span className="min-w-[20px] text-center text-sm font-bold text-slate-900 dark:text-white">
        {reglement.display_order ?? 0}
      </span>

      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => onMoveUp(reglement.id)}
          disabled={index === 0 || isOrdering}
          className={`transition ${
            index === 0 || isOrdering
              ? "cursor-not-allowed text-slate-300"
              : "text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200"
          }`}
          title="Monter"
        >
          <TbTriangleFilled />
        </button>

        <button
          type="button"
          onClick={() => onMoveDown(reglement.id)}
          disabled={index === total - 1 || isOrdering}
          className={`transition ${
            index === total - 1 || isOrdering
              ? "cursor-not-allowed text-slate-300"
              : "text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200"
          }`}
          title="Descendre"
        >
          <TbTriangleInvertedFilled />
        </button>
      </div>
    </div>
  );
}

function LinkBadge({ href, label }) {
  if (!href) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
        {label} absent
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300"
    >
      <FaExternalLinkAlt className="text-[10px]" />
      {label}
    </a>
  );
}

function DesktopTable({
  rows,
  loading,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  indexById,
  totalFiltered,
  isOrdering,
  onCreate,
}) {
  return (
    <div className="hidden lg:block rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      {loading ? (
        <div className="px-4 py-10 text-center text-slate-500">Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="p-6">
          <EmptyState onCreate={onCreate} />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-3xl">
          <table className="w-full min-w-[1240px] table-fixed text-sm">
            <thead className="bg-slate-50 text-left dark:bg-slate-950/60">
              <tr className="text-slate-600 dark:text-slate-300">
                <th className="w-[34%] px-6 py-3 font-semibold">Règlement</th>
                <th className="w-[16%] px-6 py-3 font-semibold">Aperçu</th>
                <th className="w-[14%] px-6 py-3 font-semibold">Ordre</th>
                <th className="w-[20%] px-6 py-3 font-semibold">Liens</th>
                <th className="w-[16%] px-6 py-3 font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {rows.map((reglement) => {
                const index = indexById[reglement.id] ?? 0;
                const previewText = stripHtml(reglement.content);

                return (
                  <tr
                    key={reglement.id}
                    className="align-top transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 text-white shadow-sm">
                          <FaBookOpen />
                        </div>

                        <div className="min-w-0">
                          <div className="text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
                            {reglement.title || "Sans titre"}
                          </div>

                          <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                            ID #{reglement.id}
                          </div>

                          {previewText ? (
                            <div className="mt-2 line-clamp-3 text-[12px] text-slate-600 dark:text-slate-300">
                              {short(previewText, 180)}
                            </div>
                          ) : (
                            <div className="mt-2 text-[12px] italic text-slate-400">
                              Aucun contenu.
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <PreviewImage
                        src={reglement.lien_url_image}
                        alt={reglement.title}
                      />
                    </td>

                    <td className="px-6 py-4">
                      <OrderControl
                        reglement={reglement}
                        index={index}
                        total={totalFiltered}
                        isOrdering={isOrdering}
                        onMoveUp={onMoveUp}
                        onMoveDown={onMoveDown}
                      />
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start gap-2">
                        <LinkBadge
                          href={reglement.lien_url}
                          label="Lien du règlement"
                        />
                        <LinkBadge
                          href={reglement.lien_url_image}
                          label="Image / visuel"
                        />
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          title="Éditer"
                          onClick={() => onEdit(reglement)}
                          className="inline-flex h-10 items-center gap-2 rounded-2xl bg-indigo-600 px-4 text-sm font-semibold text-white transition hover:bg-indigo-700"
                        >
                          <FaPen />
                        </button>

                        <button
                          type="button"
                          title="Supprimer"
                          onClick={() => onDelete(reglement)}
                          className="inline-flex h-10 items-center gap-2 rounded-2xl bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MobileCards({
  rows,
  loading,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  indexById,
  totalFiltered,
  isOrdering,
  onCreate,
}) {
  return (
    <div className="space-y-3 lg:hidden">
      {loading ? (
        <div className="rounded-3xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Chargement…
        </div>
      ) : rows.length === 0 ? (
        <EmptyState onCreate={onCreate} />
      ) : (
        rows.map((reglement) => {
          const index = indexById[reglement.id] ?? 0;
          const previewText = stripHtml(reglement.content);

          return (
            <div
              key={reglement.id}
              className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 text-white shadow-sm">
                  <FaBookOpen />
                </div>

                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">
                    {reglement.title || "Sans titre"}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    ID #{reglement.id}
                  </p>

                  {previewText ? (
                    <p className="mt-2 line-clamp-3 text-sm text-slate-600 dark:text-slate-300">
                      {short(previewText, 180)}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm italic text-slate-400">
                      Aucun contenu.
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <PreviewImage
                  src={reglement.lien_url_image}
                  alt={reglement.title}
                />
              </div>

              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Ordre
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-lg font-black text-slate-900 dark:text-white">
                      {reglement.display_order ?? 0}
                    </span>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => onMoveUp(reglement.id)}
                        disabled={index === 0 || isOrdering}
                        className={`flex h-8 w-8 items-center justify-center rounded-xl ${
                          index === 0 || isOrdering
                            ? "cursor-not-allowed bg-slate-100 text-slate-300 dark:bg-slate-900"
                            : "border border-slate-200 bg-white text-blue-600 dark:border-slate-700 dark:bg-slate-950 dark:text-blue-300"
                        }`}
                      >
                        <TbTriangleFilled />
                      </button>

                      <button
                        type="button"
                        onClick={() => onMoveDown(reglement.id)}
                        disabled={index === totalFiltered - 1 || isOrdering}
                        className={`flex h-8 w-8 items-center justify-center rounded-xl ${
                          index === totalFiltered - 1 || isOrdering
                            ? "cursor-not-allowed bg-slate-100 text-slate-300 dark:bg-slate-900"
                            : "border border-slate-200 bg-white text-blue-600 dark:border-slate-700 dark:bg-slate-950 dark:text-blue-300"
                        }`}
                      >
                        <TbTriangleInvertedFilled />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Liens
                  </div>

                  <div className="mt-2 flex flex-col items-start gap-2">
                    <LinkBadge
                      href={reglement.lien_url}
                      label="Lien du règlement"
                    />
                    <LinkBadge
                      href={reglement.lien_url_image}
                      label="Image / visuel"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  title="Éditer"
                  onClick={() => onEdit(reglement)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-3 text-sm font-semibold text-white transition hover:bg-indigo-700"
                >
                  <FaPen />
                </button>

                <button
                  type="button"
                  title="Supprimer"
                  onClick={() => onDelete(reglement)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 text-sm font-semibold text-white transition hover:bg-red-700"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function PaginationBar({ page, pageCount, setPage, loading }) {
  if (pageCount <= 1) return null;

  return (
    <div className="mt-5 flex flex-col items-center justify-between gap-3 rounded-3xl border border-slate-200 bg-white px-4 py-4 sm:flex-row dark:border-slate-800 dark:bg-slate-900">
      <div className="text-sm text-slate-500 dark:text-slate-400">
        Page{" "}
        <span className="font-semibold text-slate-900 dark:text-white">
          {page}
        </span>{" "}
        sur{" "}
        <span className="font-semibold text-slate-900 dark:text-white">
          {pageCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1 || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaChevronLeft />
          Précédent
        </button>

        <button
          type="button"
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={page === pageCount || loading}
          className="inline-flex h-10 items-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Suivant
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}

/* ================= Page ================= */
const ReglementsArbitragePage = () => {
  useScrollToTop();
  const { addNotification } = useNotification();

  const [allReglements, setAllReglements] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReglement, setSelectedReglement] = useState(null);
  const [modalMode, setModalMode] = useState("create");

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 300);

  const [page, setPage] = useState(1);
  const [isOrdering, setIsOrdering] = useState(false);

  const fetchReglements = async () => {
    try {
      setLoading(true);
      const data = await getReglementsArbitrage();
      setAllReglements(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des règlements d'arbitrage",
        error
      );
      addNotification(
        "Erreur lors de la récupération des règlements d'arbitrage.",
        "error"
      );
      setAllReglements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReglements();
  }, []);

  const filteredReglements = useMemo(
    () => applyFiltersAndSort(allReglements, debouncedSearch),
    [allReglements, debouncedSearch]
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const totalReglements = allReglements.length;
  const withLinksCount = allReglements.filter((r) => !!r.lien_url).length;
  const withImagesCount = allReglements.filter((r) => !!r.lien_url_image).length;
  const textOnlyCount = allReglements.filter(
    (r) => !r.lien_url && !r.lien_url_image
  ).length;

  const pageCount = Math.max(1, Math.ceil(filteredReglements.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  const paginatedReglements = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredReglements.slice(start, start + PAGE_SIZE);
  }, [filteredReglements, safePage]);

  const indexById = useMemo(() => {
    return filteredReglements.reduce((acc, item, idx) => {
      acc[item.id] = idx;
      return acc;
    }, {});
  }, [filteredReglements]);

  const openCreateModal = () => {
    setSelectedReglement(null);
    setModalMode("create");
    setIsModalOpen(true);
  };

  const openEditModal = (reglement) => {
    setSelectedReglement(reglement);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedReglement(null);
    setIsModalOpen(false);
  };

  const handleDeleteReglement = async (reglement) => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer le règlement "${reglement.title}" ?`
      )
    ) {
      return;
    }

    try {
      const result = await deleteReglementArbitrage(reglement.id);

      if (!result) {
        throw new Error("Suppression impossible");
      }

      addNotification("Règlement supprimé avec succès.", "success");
      setAllReglements((prev) => prev.filter((r) => r.id !== reglement.id));
    } catch (error) {
      console.error(
        "Erreur lors de la suppression d’un règlement d'arbitrage",
        error
      );
      addNotification(
        "Erreur lors de la suppression d’un règlement d'arbitrage.",
        "error"
      );
    }
  };

  const moveReglement = async (reglementId, direction) => {
    if (isOrdering) return;
    setIsOrdering(true);

    const prevList = [...allReglements];

    try {
      const currentFiltered = applyFiltersAndSort(allReglements, debouncedSearch);
      const idx = currentFiltered.findIndex((r) => r.id === reglementId);
      if (idx === -1) return;

      const targetIdx = direction === "up" ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= currentFiltered.length) return;

      const currentId = currentFiltered[idx].id;
      const targetId = currentFiltered[targetIdx].id;

      const list = [...allReglements];
      const i = list.findIndex((r) => r.id === currentId);
      const j = list.findIndex((r) => r.id === targetId);
      if (i === -1 || j === -1) return;

      const tmp = Number(list[i].display_order ?? 0);
      list[i].display_order = Number(list[j].display_order ?? 0);
      list[j].display_order = tmp;

      list.sort((a, b) => {
        const orderA = Number(a.display_order ?? 0);
        const orderB = Number(b.display_order ?? 0);
        if (orderA !== orderB) return orderA - orderB;
        return Number(a.id ?? 0) - Number(b.id ?? 0);
      });

      setAllReglements(list);

      const payload = list.map((item, index) => ({
        id: item.id,
        display_order: Number(item.display_order ?? index + 1),
      }));

      const reordered = await reorderReglementsArbitrage(payload);

      if (Array.isArray(reordered) && reordered.length > 0) {
        setAllReglements(reordered);
      }

      addNotification("Ordre des règlements mis à jour.", "success");
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour de l'ordre des règlements",
        error
      );
      addNotification(
        "Erreur lors de la mise à jour de l'ordre des règlements.",
        "error"
      );
      setAllReglements(prevList);
    } finally {
      setIsOrdering(false);
    }
  };

  const moveReglementUp = (reglementId) => moveReglement(reglementId, "up");
  const moveReglementDown = (reglementId) => moveReglement(reglementId, "down");

  const handleReset = () => {
    setSearchTerm("");
    setPage(1);
  };

  return (
    <div className="w-full pb-10 pt-[96px] font-satoshi">
      <div className="mx-auto max-w-[1400px] px-4">
        <AdminPageHeader
          totalReglements={totalReglements}
          withLinksCount={withLinksCount}
          withImagesCount={withImagesCount}
          textOnlyCount={textOnlyCount}
        />

        <div className="mt-5">
          <FiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onReset={handleReset}
            onRefresh={fetchReglements}
            onCreate={openCreateModal}
            loading={loading}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {filteredReglements.length} règlement
            {filteredReglements.length > 1 ? "s" : ""}
          </span>

          {debouncedSearch && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Recherche : {searchTerm}
            </span>
          )}

          {!loading && filteredReglements.length > 0 && (
            <span className="ml-auto text-xs">
              Affichage {paginatedReglements.length} • page {safePage} /{" "}
              {pageCount}
            </span>
          )}
        </div>

        <div className="mt-5">
          <DesktopTable
            rows={paginatedReglements}
            loading={loading}
            onEdit={openEditModal}
            onDelete={handleDeleteReglement}
            onMoveUp={moveReglementUp}
            onMoveDown={moveReglementDown}
            indexById={indexById}
            totalFiltered={filteredReglements.length}
            isOrdering={isOrdering}
            onCreate={openCreateModal}
          />

          <MobileCards
            rows={paginatedReglements}
            loading={loading}
            onEdit={openEditModal}
            onDelete={handleDeleteReglement}
            onMoveUp={moveReglementUp}
            onMoveDown={moveReglementDown}
            indexById={indexById}
            totalFiltered={filteredReglements.length}
            isOrdering={isOrdering}
            onCreate={openCreateModal}
          />
        </div>

        <PaginationBar
          page={safePage}
          pageCount={pageCount}
          setPage={setPage}
          loading={loading}
        />
      </div>

      {isModalOpen && (
        <ReglementArbitrageModal
          isActive={isModalOpen}
          onClose={closeModal}
          refreshReglements={fetchReglements}
          mode={modalMode}
          reglementData={selectedReglement}
        />
      )}
    </div>
  );
};

export default ReglementsArbitragePage;